import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { adminHomeDashboardSliceAction } from "../../../redux/slices/admin-home-dashboard";
import {
  EMPTY_INTEGRATION_OVERVIEW,
  EMPTY_PARTNER_ECOSYSTEM,
  EMPTY_PARTNER_PROGRAM,
  EMPTY_PENDING_ACTIONS,
  EMPTY_PS_REVENUE,
  mapIntegrationOverview,
  mapPartnerEcosystem,
  mapPartnerProgram,
  mapPendingActions,
  mapPsRevenue,
  parseEvaluationYear,
} from "../../../redux/slices/admin-home-dashboard/adminHomeDashboardMapper";
import { getAdminHomeDashboardSagaAction } from "../../../redux/saga/admin-home-dashboard/saga-actions";
import { getRevenueStreamSagaAction } from "../../../redux/saga/partner-details/saga-actions";
import type {
  IntegrationOverviewData,
  PartnerEcosystemData,
  PartnerProgramData,
  PendingActionData,
  PsRevenueData,
} from "../types";
import { mapFiltersToQueryParams } from "../utils/filterParams";
import {
  BASED_ON_OPTIONS,
  buildGroupedFilterOptions,
  DEFAULT_BASED_ON_OPTION,
  normalizeFilterSelection,
} from "../utils/filterOptions";
import type { AdminHomeDropdownOption } from "../utils/adminHomeDropdownStyles";

export type AdminHomeDashboardViewData = {
  pendingActions: PendingActionData[];
  partnerEcosystem: PartnerEcosystemData;
  partnerProgram: PartnerProgramData;
  psRevenue: PsRevenueData;
  integrationOverview: IntegrationOverviewData;
  isLoading: boolean;
  error: string | null;
  basedOnOptions: AdminHomeDropdownOption[];
  filterOptionGroups: ReturnType<typeof buildGroupedFilterOptions>;
  isFilterEnabled: boolean;
  basedOn: AdminHomeDropdownOption | null;
  filters: AdminHomeDropdownOption[];
  setBasedOn: (option: AdminHomeDropdownOption | null) => void;
  setFilters: (options: AdminHomeDropdownOption[]) => void;
};

export function useAdminHomeDashboardData(): AdminHomeDashboardViewData {
  const dispatch = useAppDispatch();
  const { data, status } = useAppSelector(
    (state) => state.admin_home_dashboard
  );
  const revenueStreams = useAppSelector(
    (state) => state.partner_details.revenueStream
  );

  const [basedOn, setBasedOnState] = useState<AdminHomeDropdownOption | null>(
    DEFAULT_BASED_ON_OPTION
  );
  const [filters, setFiltersState] = useState<AdminHomeDropdownOption[]>([]);

  const basedOnValue = basedOn?.value ?? null;

  const filterOptionGroups = useMemo(
    () => buildGroupedFilterOptions(basedOnValue, revenueStreams),
    [basedOnValue, revenueStreams]
  );

  const isFilterEnabled = basedOnValue != null && basedOnValue !== "select";

  useEffect(() => {
    if (revenueStreams.length === 0) {
      dispatch(getRevenueStreamSagaAction());
    }
  }, [dispatch, revenueStreams.length]);

  const queryParams = useMemo(
    () =>
      mapFiltersToQueryParams({
        basedOnValue,
        filterValues: filters.map((item) => item.value),
      }),
    [basedOnValue, filters]
  );

  const evaluationYear = useMemo(
    () => parseEvaluationYear(queryParams.date),
    [queryParams.date]
  );

  useEffect(() => {
    dispatch(getAdminHomeDashboardSagaAction(queryParams));
  }, [dispatch, queryParams]);

  useEffect(
    () => () => {
      dispatch(adminHomeDashboardSliceAction.clearAdminHomeDashboard());
    },
    [dispatch]
  );

  const setBasedOn = useCallback((option: AdminHomeDropdownOption | null) => {
    setBasedOnState(option);
    setFiltersState([]);
  }, []);

  const setFilters = useCallback(
    (options: AdminHomeDropdownOption[]) => {
      setFiltersState(normalizeFilterSelection(options, basedOnValue));
    },
    [basedOnValue]
  );

  const pendingActions = useMemo(() => {
    if (!data?.pending_actions) {
      return EMPTY_PENDING_ACTIONS;
    }

    return mapPendingActions(data.pending_actions);
  }, [data?.pending_actions]);

  const partnerEcosystem = useMemo(() => {
    if (!data?.partner_ecosystem) {
      return EMPTY_PARTNER_ECOSYSTEM;
    }

    return mapPartnerEcosystem(data.partner_ecosystem);
  }, [data?.partner_ecosystem]);

  const partnerProgram = useMemo(() => {
    if (!data?.partner_program) {
      return EMPTY_PARTNER_PROGRAM(evaluationYear);
    }

    return mapPartnerProgram(data.partner_program, evaluationYear);
  }, [data?.partner_program, evaluationYear]);

  const psRevenue = useMemo(() => {
    if (!data?.ps_revenue) {
      return EMPTY_PS_REVENUE(evaluationYear);
    }

    return mapPsRevenue(data.ps_revenue, evaluationYear);
  }, [data?.ps_revenue, evaluationYear]);

  const integrationOverview = useMemo(() => {
    if (!data?.integration_overview) {
      return EMPTY_INTEGRATION_OVERVIEW;
    }

    return mapIntegrationOverview(data.integration_overview);
  }, [data?.integration_overview]);

  return {
    pendingActions,
    partnerEcosystem,
    partnerProgram,
    psRevenue,
    integrationOverview,
    isLoading: status.loading && !data,
    error: status.error,
    basedOnOptions: BASED_ON_OPTIONS,
    filterOptionGroups,
    isFilterEnabled,
    basedOn,
    filters,
    setBasedOn,
    setFilters,
  };
}
